// Radar parle directement à l'extension du store (issue #234), par
// `chrome.runtime.sendMessage(ID_EXTENSION, …)`. Le protocole est dans
// `@ai-job-fit/engine/pontExtension` ; la réponse de l'extension dans
// `apps/extension/src/lib/pontRadarExterne.ts`.
//
// RIEN N'Y EST GARANTI, et tout se replie en silence sur le parcours d'avant :
// un autre navigateur que Chrome n'a pas `chrome.runtime`, une extension
// chargée depuis le zip a un autre identifiant, une version d'avant ne
// déclare pas `externally_connectable`. Dans ces cas, la sonde rend `null`
// et l'écran garde ses boutons « installer » et « connecter par code ».

import { useEffect, useState } from 'react'
import { ID_EXTENSION } from '@ai-job-fit/engine/webStore'
import {
  EXT_CONNECTER,
  EXT_CV,
  EXT_PING,
  type EtatExtensionExterne,
  type MessageExterne,
  type ReponseConnexionExterne,
  type ReponseCvExterne,
} from '@ai-job-fit/engine/pontExtension'
import { post } from './api'

type Runtime = {
  sendMessage: (id: string, message: unknown, rappel: (reponse: unknown) => void) => void
  lastError?: unknown
}

function runtime(): Runtime | null {
  const r = (globalThis as { chrome?: { runtime?: Runtime } }).chrome?.runtime
  return r && typeof r.sendMessage === 'function' ? r : null
}

/** Un message à l'extension. `null` si elle ne répond pas, quelle qu'en soit
 *  la raison ; l'échange d'un code peut prendre quelques secondes. */
function ecrire<T>(message: MessageExterne, delaiMs: number): Promise<T | null> {
  const r = runtime()
  if (!r) return Promise.resolve(null)
  return new Promise((resolve) => {
    const minuterie = setTimeout(() => resolve(null), delaiMs)
    try {
      r.sendMessage(ID_EXTENSION, message, (reponse) => {
        clearTimeout(minuterie)
        // Lire `lastError` le consomme : sans ça, Chrome écrit une erreur
        // dans la console à chaque visite d'un navigateur sans l'extension.
        resolve(r.lastError ? null : ((reponse as T) ?? null))
      })
    } catch {
      clearTimeout(minuterie)
      resolve(null)
    }
  })
}

export function sonderExtension(): Promise<EtatExtensionExterne | null> {
  return ecrire<EtatExtensionExterne>({ type: EXT_PING }, 1500)
}

/** Le CV local de l'extension, ou `null`. */
export async function cvDeLExtension(): Promise<unknown | null> {
  const r = await ecrire<ReponseCvExterne>({ type: EXT_CV }, 3000)
  return r?.ok ? r.profile : null
}

/** Connecte l'extension au compte de cette session : un code, passé
 *  directement, que l'extension échange contre son jeton. */
export async function connecterLExtension(): Promise<ReponseConnexionExterne> {
  const { code } = await post<{ code: string }>('/api/auth/extension/code', {})
  return (await ecrire<ReponseConnexionExterne>({ type: EXT_CONNECTER, code }, 20000)) ?? { ok: false, raison: 'reseau' }
}

/** L'état de l'extension installée, relu quand `cle` change. `undefined`
 *  pendant la sonde, `null` si aucune ne répond. */
export function useExtensionInstallee(cle: unknown = 0): EtatExtensionExterne | null | undefined {
  const [etat, setEtat] = useState<EtatExtensionExterne | null | undefined>(undefined)
  useEffect(() => {
    let vivant = true
    void sonderExtension().then((e) => { if (vivant) setEtat(e) })
    return () => { vivant = false }
  }, [cle])
  return etat
}
